/*
  Le devis d'une commande, cote navigateur : les types, et rien qui calcule un
  prix.

  ## La regle, en une phrase

  **Aucun montant de ce fichier n'est calcule ici.** Tous viennent de
  `quote_order`, en base. Ce module ne sait que les mettre en forme.

  C'est la correction du defaut mesure le 4 septembre 2026. L'ancien panier
  portait un `price` et le recalculait lui-meme :

      let newPrice = mainPrice;
      if (newPriceType === 'reduced' || newPriceType === 'local_reduced') {
        newPrice = reducedPrice || mainPrice;   // <- retombe au tarif NORMAL
      }

  `reducedPrice` venait d'un `sessionStorage` ecrit au moment de l'ajout au
  panier. Pour le second enfant, la cle n'y etait pas : le type passait bien en
  « reduit » et le prix restait le normal. 221 inscriptions facturees a un
  tarif qui existe, mais pas le leur, pour 7 298,00 EUR.

  Le panier ne porte donc plus de prix du tout — voir `src/stores/cartStore.ts`.
*/

/** Un tarif tel que la base le nomme. */
/* `late` et `late_reduced` : une journée pédagogique après sa date limite. */
export type TypeDeTarif = 'normal' | 'reduced' | 'local' | 'local_reduced' | 'late' | 'late_reduced';

/** Une ligne chiffree, telle que `quote_order` la rend. */
export interface LigneDevis {
  session_id: string;
  kid_id: string;
  price_type: TypeDeTarif;
  /** Le plein tarif de la semaine, en centimes. Jamais diminue par un bon. */
  amount_cents: number;
  period_id: string | null;
  gratuity_code_id: string | null;
  /** Le code tel que le parent l'a tape, pour le lui montrer sur la bonne ligne. */
  gratuity_code: string | null;
  /** Ce que le bon retire de la facture, en centimes. 0 si aucun. */
  gratuity_cents: number;
}

/** Le devis complet. Tous les montants sont en centimes. */
export interface Devis {
  gross_cents: number;
  gratuity_cents: number;
  /** Ce que la facture reclamera : brut moins les gratuites. */
  amount_cents: number;
  provision_available_cents: number;
  /** Ce que la provision paiera d'office. */
  provision_applicable_cents: number;
  /** Ce qu'il restera a payer. C'est CE chiffre qui part vers Stripe. */
  remaining_cents: number;
  lines: LigneDevis[];
}

/** La commande passee, telle que `create_order` la rend. */
export interface Commande extends Omit<Devis, 'provision_available_cents' | 'provision_applicable_cents'> {
  invoice_id: string;
  invoice_number: string;
  /**
   * La communication structurée, `+++100/2602/02433+++`.
   *
   * C'est elle que le parent recopie dans son virement, et elle seule : sa
   * banque la vérifie à la saisie, clé modulo 97 à l'appui. Le numéro de
   * facture, lui, n'est vérifié par personne.
   */
  communication: string;
  provision_applied_cents: number;
  registration_ids: string[];
  declaration_id: string | null;
}

/*
  Le seuil de la declaration sur l'honneur : le RMMMG belge.

  Il est ecrit ICI et nulle part ailleurs. Il change avec l'index : le jour ou
  il changera, une seule ligne bougera — et les declarations deja signees
  garderont le seuil qui leur a ete montre, puisque
  `reduced_tariff_declarations` le conserve ligne par ligne.

  L'ecran affichait encore 2 070,48 EUR, un montant perime.
*/
export const SEUIL_REVENUS_CENTIMES = 223361;

export function texteDeclaration(seuilCentimes = SEUIL_REVENUS_CENTIMES): string {
  return (
    `Je déclare sur l'honneur que les revenus de mon ménage sont inférieurs ` +
    `au revenu minimum mensuel moyen garanti belge, soit ${euros(seuilCentimes)} ` +
    `net par mois. Je comprends que des justificatifs pourront m'être demandés ` +
    `et que toute fausse déclaration pourra entraîner l'annulation de l'inscription.`
  );
}

/*
  Le formatage des montants. `Intl` insere une espace fine insecable (U+202F)
  que la police WinAnsi des PDF ne connait pas ; ici on est dans un navigateur,
  donc c'est sans danger — mais on garde la meme forme que le PDF pour que les
  deux se lisent pareil.
*/
export function euros(centimes: number | null | undefined): string {
  const c = Math.round(centimes ?? 0);
  const signe = c < 0 ? '-' : '';
  const abs = Math.abs(c);
  const entier = Math.floor(abs / 100).toString();
  const decimales = (abs % 100).toString().padStart(2, '0');

  let groupe = '';
  for (let i = 0; i < entier.length; i++) {
    if (i > 0 && (entier.length - i) % 3 === 0) groupe += ' ';
    groupe += entier[i];
  }
  return `${signe}${groupe},${decimales} €`;
}

const LIBELLES: Record<TypeDeTarif, string> = {
  normal: 'Tarif normal',
  reduced: 'Tarif réduit',
  local: 'Tarif local',
  local_reduced: 'Tarif local réduit',
  late: 'Après la date limite',
  late_reduced: 'Réduit, après la date limite',
};

export function libelleTarif(type: TypeDeTarif): string {
  return LIBELLES[type] ?? 'Tarif normal';
}

/** Vrai si le tarif reduit s'applique reellement a cette ligne. */
export function estReduit(type: TypeDeTarif): boolean {
  return type === 'reduced' || type === 'local_reduced' || type === 'late_reduced';
}

/** Vrai si le tarif local s'applique a cette ligne. */
export function estLocal(type: TypeDeTarif): boolean {
  return type === 'local' || type === 'local_reduced';
}

/*
  Ce que l'ecran doit annoncer, en une phrase, sous le total.

  Trois cas a distinguer, parce qu'ils n'appellent pas la meme action du
  parent : payer le tout, payer un reste, ou n'avoir rien a payer.
*/
export function phraseDeReste(devis: Devis): string {
  if (devis.remaining_cents <= 0) {
    if (devis.provision_applicable_cents > 0) {
      return `Rien à payer : votre provision de ${euros(devis.provision_applicable_cents)} couvre la totalité.`;
    }
    return 'Rien à payer.';
  }
  if (devis.provision_applicable_cents > 0) {
    return (
      `${euros(devis.provision_applicable_cents)} seront prélevés sur votre provision. ` +
      `Reste à payer : ${euros(devis.remaining_cents)}.`
    );
  }
  return `Montant à payer : ${euros(devis.remaining_cents)}.`;
}

/*
  Un code de gratuite, normalise. Le parent tape en minuscules, avec des
  espaces, parfois en collant depuis un courriel. La base compare sur
  `upper(btrim(code))` : on fait pareil ici pour que la liste affichee et celle
  envoyee soient la meme, et pour reperer un doublon avant l'aller-retour.
*/
export function normaliserCode(code: string): string {
  return code.trim().toUpperCase();
}

/**
 * Ajoute un code a la liste, sans doublon. Rend la liste inchangee si le code
 * est vide ou deja present — presente deux fois, la base le refuserait, et un
 * refus qu'on peut eviter avant l'envoi vaut mieux qu'un message d'erreur.
 */
export function ajouterCode(codes: string[], brut: string): string[] {
  const code = normaliserCode(brut);
  if (!code) return codes;
  if (codes.includes(code)) return codes;
  return [...codes, code];
}

export function retirerCode(codes: string[], code: string): string[] {
  const cible = normaliserCode(code);
  return codes.filter((c) => c !== cible);
}

/*
  Le message d'erreur a montrer. `quote_order` et `create_order` rendent des
  phrases francaises destinees a etre affichees telles quelles — « le code X a
  deja ete utilise », « la semaine du 19/10/2026 n'a plus assez de places ».

  Ce qu'il faut eviter, c'est de laisser passer un message de base de donnees
  brut (« duplicate key value violates unique constraint ... »), qui ferait
  appeler le secretariat. On reconnait les notres a l'absence de vocabulaire
  technique.
*/
const TECHNIQUE = [
  'violates',
  'constraint',
  'syntax error',
  'permission denied',
  'relation ',
  'column ',
  'JWT',
  'Failed to fetch',
];

/*
  Les refus que la BASE exprime en langage de base de données, et qui ont
  pourtant une phrase à dire au parent.

  Le 9 septembre 2026, une réinscription après un paiement Stripe abandonné
  échouait sur `registrations_kid_id_activity_id_key`, et le parent lisait
  « Une erreur technique est survenue » — un message qui ne lui apprend rien et
  l'envoie appeler le secrétariat. La cause est corrigée par la migration
  20260909214245 : la contrainte est devenue un index PARTIEL, et une
  inscription annulée ne bloque plus le retour.

  Reste la course : deux commandes simultanées pour le même enfant et la même
  semaine passent toutes deux le `EXISTS` de `create_order`, et c'est l'index
  qui arbitre. Le perdant mérite la même phrase que le refus ordinaire — il
  décrit exactement la même situation.

  Cette table se lit AVANT le filtre technique : c'est ce qui la rend utile.
*/
const REFUS_CONNUS: { motif: RegExp; phrase: string }[] = [
  {
    motif: /registrations_une_inscription_active|registrations_kid_id_activity_id_key/i,
    phrase: "Cet enfant est déjà inscrit à cette semaine.",
  },
];

export function messageAffichable(brut: string | null | undefined): string {
  const m = (brut ?? '').trim();
  if (!m) return "Une erreur est survenue. Merci de réessayer.";

  const connu = REFUS_CONNUS.find((r) => r.motif.test(m));
  if (connu) return connu.phrase;

  if (TECHNIQUE.some((t) => m.toLowerCase().includes(t.toLowerCase()))) {
    return "Une erreur technique est survenue. Merci de contacter le secrétariat.";
  }
  return m.charAt(0).toUpperCase() + m.slice(1);
}

/*
  Ce que la base reproche à un code, et ce que le parent doit en faire.

  Les fonctions rendent des phrases françaises — « le code « BIGGY » n'existe
  pas », « le code « AUT-2026 » a deja ete utilise ». Elles sont justes, mais
  elles laissent le parent devant un écran bloqué : le récapitulatif disparaît,
  et rien ne lui dit quel code retirer ni où.

  Ce module reconnaît le code en cause et la nature du refus, pour que l'écran
  puisse proposer le geste — retirer CE code — au lieu d'un message seul.
*/
export type MotifDeRefus =
  | 'inconnu'
  | 'deja_utilise'
  | 'expire'
  | 'pas_encore_valable'
  | 'autre_famille'
  | 'aucune_semaine'
  | 'presente_deux_fois'
  | 'indetermine';

export interface RefusDeCode {
  /** Le code en cause, tel que la base l'a cité. `null` si elle n'en cite aucun. */
  code: string | null;
  motif: MotifDeRefus;
  /** Le message à afficher, en français, sans jargon. */
  message: string;
  /** Ce que le parent peut faire, quand il y a quelque chose à faire. */
  conseil: string | null;
}

/*
  `c` est le code cité, ou `null` quand la base n'en cite aucun. Les phrases
  s'écrivent donc dans les deux cas : « Le code « BIGGY » n'existe pas » et
  « Ce code n'existe pas ». Fabriquer un faux « ce code » entre guillemets
  ferait croire à un code réellement saisi.
*/
const MOTIFS: [RegExp, MotifDeRefus, (c: string | null) => { message: string; conseil: string | null }][] = [
  [/n'?existe pas/i, 'inconnu', (c) => ({
    message: c ? `Le code « ${c} » n'existe pas.` : "Ce code n'existe pas.",
    conseil: 'Vérifiez la saisie — un zéro et la lettre O se ressemblent, tout comme le 1 et le I.',
  })],
  [/d[ée]j[àa] [ée]t[ée] utilis[ée]/i, 'deja_utilise', (c) => ({
    message: c ? `Le code « ${c} » a déjà été utilisé.` : 'Ce code a déjà été utilisé.',
    conseil: "Un code cadeau ne vaut que pour une seule semaine, une seule fois. S'il vous semble ne pas l'avoir utilisé, contactez le secrétariat.",
  })],
  [/a expir[ée]/i, 'expire', (c) => ({
    message: c ? `Le code « ${c} » est arrivé à échéance.` : 'Ce code est arrivé à échéance.',
    conseil: 'Contactez le secrétariat si vous pensez que la date est erronée.',
  })],
  [/ne sera utilisable/i, 'pas_encore_valable', (c) => ({
    message: c ? `Le code « ${c} » n'est pas encore utilisable.`
               : "Ce code n'est pas encore utilisable.",
    conseil: 'Il le deviendra à la date indiquée sur le bon.',
  })],
  [/autre famille/i, 'autre_famille', (c) => ({
    message: c ? `Le code « ${c} » a été émis pour une autre famille.`
               : 'Ce code a été émis pour une autre famille.',
    conseil: 'Les codes nominatifs ne sont pas transmissibles.',
  })],
  [/aucune semaine de ce panier/i, 'aucune_semaine', (c) => ({
    message: c ? `Le code « ${c} » ne s'applique à aucune semaine de votre panier.`
               : "Ce code ne s'applique à aucune semaine de votre panier.",
    conseil: "Soit il ne couvre pas ces périodes, soit toutes vos semaines sont déjà offertes par un autre code.",
  })],
  [/pr[ée]sent[ée] deux fois/i, 'presente_deux_fois', (c) => ({
    message: c ? `Le code « ${c} » est présent deux fois.` : 'Ce code est présent deux fois.',
    conseil: "Un code ne rend gratuite qu'une seule semaine. Retirez-en une occurrence.",
  })],
];

/**
 * Lit le refus d'un code dans le message de la base.
 *
 * Rend `null` si le message ne concerne pas un code — une semaine complète, un
 * enfant qui n'appartient pas à la famille : ces refus-là n'appellent pas le
 * même geste.
 */
export function lireRefusDeCode(brut: string | null | undefined): RefusDeCode | null {
  const m = (brut ?? '').trim();
  if (!m || !/code/i.test(m)) return null;

  /* La base cite le code entre guillemets français : « AUT-2026 ». */
  const cite = m.match(/«\s*([^»]+?)\s*»/);
  const code = cite ? cite[1].trim() : null;

  for (const [motif, nom, fabrique] of MOTIFS) {
    if (motif.test(m)) {
      const { message, conseil } = fabrique(code);
      return { code, motif: nom, message, conseil };
    }
  }

  /*
    Un refus qui parle d'un code sans qu'on reconnaisse le motif : on rend
    quand même le code, pour que l'écran propose de le retirer. Mieux vaut un
    geste possible qu'un message seul.
  */
  return code ? { code, motif: 'indetermine', message: messageAffichable(m), conseil: null } : null;
}
